"use client"

import styled from "@emotion/styled"
import { Tab, Tabs } from "@mui/material"
import { Dispatch, SetStateAction, useEffect } from "react"
import { useInView } from "react-intersection-observer"
import Container from "ui/Container"
import {
  delayTime,
  transitionEase,
  transitionTime,
  useInViewConfig,
} from "ui/transitions/utils"

const StyledLayout = styled.div`
  display: flex;
  flex-direction: column;
  gap: 32px;
  ${(p) => p.theme.breakpoints.up("md")} {
    gap: 48px;
  }
`

const StyledTabs = styled(Tabs, {
  shouldForwardProp: (prop) => prop !== "$inview",
})<{ $inview: boolean }>`
  opacity: ${(p) => (p.$inview ? 1 : 0)};
  transition: all ${transitionTime}s ${transitionEase};
  transform: ${(p) => (p.$inview ? "translateX(0px)" : `translateX(-100vw)`)};
  transition-delay: ${delayTime}s;
`

const StyledTab = styled(Tab)`
  font-weight: 400;
  font-size: 20px;
  line-height: 140%;
  &.Mui-selected {
    font-weight: 700;
  }
  margin: 5px;
  &:focus-visible {
    border: 1px dashed white;
    outline-offset: 2px;
    outline: 2px solid black;
  }
`

function a11yProps(index: number) {
  return {
    id: `simple-tab-${index}`,
    "aria-controls": `simple-tabpanel-${index}`,
    role: "tab",
  }
}

interface TabPanelProps {
  children?: React.ReactNode
  index: number
  value: number
}

function CustomTabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      tabIndex={0}
      {...other}
    >
      {value === index && <>{children}</>}
    </div>
  )
}

export default function CommonTabs({
  data,
  value,
  setValue,
}: {
  data: { label: string; content: React.ReactElement }[]
  value: number
  setValue: Dispatch<SetStateAction<number>>
}) {
  const handleChange = (event: React.SyntheticEvent, newValue: number) => {
    setValue(newValue)
  }

  // Focus the first focusable element in the active tab panel when value changes
  useEffect(() => {
    const activePanel = document.getElementById(`simple-tabpanel-${value}`)
    if (activePanel) {
      // Small delay to ensure the panel is rendered and visible
      setTimeout(() => {
        // Find the first focusable element within the panel content
        const focusableElements = activePanel.querySelectorAll(
          'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
        )

        if (focusableElements.length > 0) {
          // Focus the first focusable element
          ;(focusableElements[0] as HTMLElement).focus()
        } else {
          // Fallback: make the panel itself focusable and focus it
          activePanel.setAttribute("tabindex", "0")
          activePanel.focus()
        }
      }, 100)
    }
  }, [value])

  const { ref, inView } = useInView({ ...useInViewConfig, threshold: 0 })

  return (
    <Container paddingMobile={true}>
      <StyledLayout ref={ref}>
        <StyledTabs
          $inview={inView}
          value={value}
          onChange={handleChange}
          aria-label="tabs"
          variant="scrollable"
          scrollButtons="auto"
          allowScrollButtonsMobile
          role="tablist"
        >
          {data.map((item, index) => (
            <StyledTab
              label={item.label}
              {...a11yProps(index)}
              key={index}
              aria-selected={index === value}
            />
          ))}
        </StyledTabs>
        {data.map((item, index) => (
          <CustomTabPanel value={value} index={index} key={index}>
            {item.content}
          </CustomTabPanel>
        ))}
      </StyledLayout>
    </Container>
  )
}
